import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { getFullUrl } from '../../../shared/utils/url';
import { DeviceResponse } from '../models/response/device-response.model';
import { DeviceParametersResponse } from '../models/response/device-parameters-response-model';
import { DeviceParameter } from '../models/device-parameter-model';
import { UpdateDeviceParametersRequest } from '../models/request/update-device-parameters-request-model';
import { CookieService } from 'ngx-cookie';
import { environment } from 'src/environments/environment';
import { DeviceList, DeviceListItem } from '../models/device-list.model';
import { DeviceTypeKeys } from '../models/device-types-attributes';
import {
  DeviceDatasheetResponse,
  UpdateDeviceLogistic,
  UpdateDeviceLogisticResponse,
} from '../models/device.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SkipErrorInterceptorHeader } from "../../../core/auth/error.interceptor";

@Injectable({
  providedIn: 'root',
})
export class DeviceService {

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private snack: MatSnackBar,
    private translate: TranslateService
  ) {
  }

  getDevicesV2(): Observable<DeviceList> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'frontend-devices-v2'
    );
    return this.http.get<DeviceList>(fullUrl).pipe(
      map((response: DeviceList) => {
        response.gates.forEach(
          (gate: DeviceListItem) =>
            (gate.externalDeviceTypeCode = DeviceTypeKeys.GATE)
        );
        response.sims.forEach(
          (sim: DeviceListItem) =>
            (sim.externalDeviceTypeCode = DeviceTypeKeys.SIMCARD)
        );
        response.szts.forEach(
          (szt: DeviceListItem) =>
            (szt.externalDeviceTypeCode = DeviceTypeKeys.SZT)
        );
        response.pdfs.forEach(
          (pdf: DeviceListItem) =>
            (pdf.externalDeviceTypeCode = DeviceTypeKeys.NONE)
        )
        return response;
      })
    );
  }

  getDeviceByExternalDeviceId(
    externalDeviceId: string
  ): Observable<DeviceResponse> {
    const fullUrl = getFullUrl(environment.guiBackendRootEndpoint, 'device');
    return this.http.get<DeviceResponse>(fullUrl, {
      params: {
        externalDeviceId: externalDeviceId,
        language: this.cookieService.get('lang')!,
      },
    });
  }

  getDeviceSensitiveDataByExternalDeviceId(
    externalDeviceId: string
  ): Observable<DeviceResponse> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-sensitive'
    );
    return this.http.post<DeviceResponse>(fullUrl, null, {
      params: {
        externalDeviceId: externalDeviceId,
        language: this.cookieService.get('lang')!,
      },
    });
  }

  getDevicesSensitiveDataByExternalDeviceIds(
    externalDeviceIds: string[]
  ): Observable<DeviceResponse[]> {
    const fullUrl = getFullUrl(environment.guiBackendRootEndpoint, 'devices-sensitive');
    return this.http.post<DeviceResponse[]>(fullUrl, externalDeviceIds, {
      params: {
        language: this.cookieService.get('lang')!,
      },
    });
  }

  getDeviceSensitiveDataStatusByExternalDeviceId(
    externalDeviceId: string,
    externalDeviceTypeCode: string
  ): Observable<Boolean> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-sensitive/status'
    );
    return this.http.post<Boolean>(fullUrl, null, {
      params: {
        externalDeviceId: externalDeviceId,
        externalDeviceTypeCode: externalDeviceTypeCode,
      },
    });
  }

  getDeviceParametersByDeviceId(
    deviceId: number
  ): Observable<DeviceParametersResponse> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-parameters'
    );
    return this.http.get<DeviceParametersResponse>(fullUrl, {
      params: {
        language: this.cookieService.get('lang')!,
        deviceId,
      },
    });
  }

  getDeviceDatasheetLink(pdfId: number): Observable<DeviceDatasheetResponse> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-datasheet'
    );

    const headers = new HttpHeaders().set(SkipErrorInterceptorHeader, '');

    return this.http.get<DeviceDatasheetResponse>(fullUrl, {
      headers : headers,
      params: {
        pdfId: pdfId,
      },
    });
  }

  getDeviceDatasheetLinkInstantly(sztExternalDeviceId: string): Observable<DeviceDatasheetResponse> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-datasheetdata'
    );

    const headers = new HttpHeaders().set(SkipErrorInterceptorHeader, '');

    return this.http.get<DeviceDatasheetResponse>(fullUrl, {
      headers : headers,
      params: {
        sztExternalDeviceId: sztExternalDeviceId,
      },
    });
  }

  updateDeviceParameters(element: DeviceParameter[]): Observable<any> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-parameters'
    );
    const body: UpdateDeviceParametersRequest = {
      deviceParameters: element,
    };
    return this.http.put<any>(fullUrl, body);
  }

  updateLogisticStatus(
    device: UpdateDeviceLogistic
  ): Observable<UpdateDeviceLogisticResponse> {
    const fullUrl = getFullUrl(
      environment.guiBackendRootEndpoint,
      'device-logistic-status'
    );
    return this.http.put<any>(fullUrl, device).pipe(
      catchError((err) => {
        this.snack.open(
          this.translate.instant('edit-error-text'),
          this.translate.instant('button.close'),
          {
            horizontalPosition: 'end',
            duration: 5000,
          }
        );
        throw err;
      })
    );
  }
}
